// Owner: design-system (plan §9.1). The brand kit on the client: the query every surface shares (the
// Branding settings, font pickers, editors that start new artefacts in the brand's default theme) and
// the uploads of logos and fonts.
import { queryOptions, useQuery } from '@tanstack/react-query';
import { listThemes } from '@dataroom/contracts/artefacts';
import {
  type BrandFontDTO,
  type BrandKitDTO,
  type BrandSurface,
  type FontStyle,
  MAX_BRAND_FONT_BYTES,
  getBrandKit,
  uploadBrandFont,
} from '@dataroom/contracts/branding';
import { api } from '../../lib/api';

export const brandKeys = {
  kit: ['brand-kit'] as const,
  themes: ['brand-kit', 'themes'] as const,
};

/** The workspace brand kit (every member may read it; administrators change it). */
export const brandKitQuery = queryOptions({
  queryKey: brandKeys.kit,
  queryFn: ({ signal }) => api.call(getBrandKit, {}, { signal }),
  staleTime: 5 * 60_000,
});

/** The workspace's themes (starter, blueprint, its own and the brand theme). */
export const themesQuery = queryOptions({
  queryKey: brandKeys.themes,
  queryFn: ({ signal }) => api.call(listThemes, { query: {} }, { signal }),
  staleTime: 60_000,
});

export function useBrandKit() {
  return useQuery(brandKitQuery);
}

/**
 * The theme key new artefacts of a surface start in: the brand kit's default for it, else
 * `fallback` (while the kit loads, or when the workspace has none).
 *
 *   const themeKey = useDefaultThemeKey('deck', 'graphite');
 */
export function useDefaultThemeKey(surface: BrandSurface, fallback: string): string {
  const kit = useBrandKit();
  return kit.data?.kit.defaults[surface] ?? fallback;
}

/** Query keys whose data shows the brand (refetched after the kit changes). */
export const BRAND_DEPENDENT_KEYS: readonly (readonly string[])[] = [
  brandKeys.themes,
  ['editor-kit', 'themes'],
];

export type FontUpload = { file: File; family?: string; weight?: number; style?: FontStyle };

const FONT_EXTENSIONS = ['.woff2', '.woff', '.ttf', '.otf'];
export const FONT_ACCEPT = [...FONT_EXTENSIONS, 'font/woff2', 'font/woff', 'font/ttf', 'font/otf'];

/** Whether a file looks like a font by name or type (the server checks the bytes). */
export function isFontFile(file: Pick<File, 'name' | 'type'>): boolean {
  const name = file.name.toLowerCase();
  return FONT_EXTENSIONS.some((e) => name.endsWith(e)) || /^font\//.test(file.type);
}

export class FontTooLargeError extends Error {
  constructor(readonly max: number) {
    super('font.too_large');
  }
}

function base64Of(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const url = String(reader.result);
      resolve(url.slice(url.indexOf(',') + 1));
    };
    reader.onerror = () => reject(reader.error ?? new Error('read'));
    reader.readAsDataURL(file);
  });
}

/** Upload one face (the uploader confirmed the licence in the dialog before). */
export async function uploadFontFile(upload: FontUpload): Promise<BrandFontDTO> {
  if (upload.file.size > MAX_BRAND_FONT_BYTES) throw new FontTooLargeError(MAX_BRAND_FONT_BYTES / 1_000_000);
  return api.call(uploadBrandFont, {
    body: {
      fileName: upload.file.name.slice(0, 160) || 'font',
      data: await base64Of(upload.file),
      licenceConfirmed: true,
      ...(upload.family ? { family: upload.family } : {}),
      ...(upload.weight ? { weight: upload.weight } : {}),
      ...(upload.style ? { style: upload.style } : {}),
    },
  });
}

/** Families of the uploaded faces, in name order, faces by weight then style. */
export function fontFamilies(
  fonts: readonly BrandFontDTO[],
): { slug: string; family: string; faces: BrandFontDTO[] }[] {
  const by = new Map<string, { slug: string; family: string; faces: BrandFontDTO[] }>();
  for (const f of fonts) {
    const g = by.get(f.slug) ?? { slug: f.slug, family: f.family, faces: [] };
    g.faces.push(f);
    by.set(f.slug, g);
  }
  return [...by.values()]
    .map((g) => ({
      ...g,
      faces: g.faces.sort((a, b) => a.weight - b.weight || a.style.localeCompare(b.style)),
    }))
    .sort((a, b) => a.family.localeCompare(b.family));
}

export type { BrandKitDTO };
